import { useLayoutEffect, useRef } from 'react';

/**
 * Fade + 16px de subida cuando la sección entra en viewport, una sola vez.
 *
 * Devuelve un ref para colgar del div raíz de la sección; el CSS vive en
 * `index.css` bajo `.js-reveal [data-reveal]`.
 *
 * Tres reglas, todas deliberadas, y las tres salen de cómo lo hace mal
 * omni2design.com (el sitio que motivó agregar esto):
 *
 * 1. El ocultamiento lo activa el JS. La clase `js-reveal` se pone en <html>
 *    recién cuando sabemos que hay `IntersectionObserver` y que el sistema no
 *    pide menos movimiento. Sin soporte, el CSS del estado oculto nunca aplica
 *    y la página se ve completa.
 * 2. `once: true` — un bloque revelado no vuelve a ocultarse nunca. El
 *    observer se desconecta en cuanto dispara. Esto es lo que evita que un
 *    back-nav o un salto por ancla devuelvan contenido al estado oculto:
 *    en Omni2 el FAQ y el bloque de equipo llegan en blanco si se salta
 *    directo a ellos en vez de bajar scrolleando.
 * 3. `prefers-reduced-motion` se respeta acá y otra vez en el CSS. La segunda
 *    cubre el caso de que alguien cambie la preferencia con la página abierta.
 *
 * Es `useLayoutEffect` y no `useEffect` a propósito: corre antes del primer
 * paint, así que el visitante nunca ve la sección aparecer, esconderse y
 * volver a aparecer. Con `useEffect` ese parpadeo es visible en la primera
 * sección bajo el hero.
 */
const ENABLED_CLASS = 'js-reveal';
const REVEALED_CLASS = 'is-revealed';

/**
 * jsdom no trae `IntersectionObserver` ni `matchMedia`, así que este guard es
 * también lo que deja los tests existentes intactos: sin observer no se agrega
 * la clase y todo renderiza visible, que es como los tests ya leen el árbol.
 */
const canReveal = () => {
  if (typeof window === 'undefined') return false;
  if (!('IntersectionObserver' in window)) return false;
  if (typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const useReveal = () => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || !canReveal()) return undefined;

    document.documentElement.classList.add(ENABLED_CLASS);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(REVEALED_CLASS);
          observer.unobserve(entry.target);
        });
      },
      // El -10% inferior atrasa un poco el disparo para que la sección entre
      // ya empezada y no justo al rozar el borde. El threshold bajo mantiene
      // el comportamiento en secciones más altas que el viewport.
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return ref;
};

export default useReveal;
